"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.E11Logger = void 0;
const constants_1 = require("./constants");
const cli_color_util_1 = require("./util/cli-color-util");
const stringify_object_1 = require("./util/stringify-object");
const dateTimeFormatter = new Intl.DateTimeFormat(undefined, {
    year: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    second: 'numeric',
    day: '2-digit',
    month: '2-digit',
});
function getLogLevel() {
    const validLevels = Object.values(constants_1.LogLevel);
    if (E11Logger.level)
        return E11Logger.level;
    else if (validLevels.includes(process.env.LOG_LEVEL))
        return process.env.LOG_LEVEL;
    else
        return constants_1.LogLevel.Debug;
}
class E11Logger {
    context;
    options;
    /** Control/override the level programmatically for all instances of E11Logger - useful on web */
    static level;
    static lastTimestampAt;
    constructor(context, options) {
        this.context = context;
        this.options = options;
    }
    log(message, ...optionalParams) {
        if (!this.levelEnabled(constants_1.LogLevel.Log))
            return;
        const { messages, context } = this.getContextAndMessagesToPrint([message, ...optionalParams]);
        this.printMessages(messages, context, constants_1.LogLevel.Log);
    }
    /**
     * ! The last non-context param must be Error to get correct error stack logging
     * e.g. new E11Logger('Test').error('Message', error) OR new E11Logger().error('Message', error, 'Context')
     * TODO: Always log errors correctly
     */
    error(message, ...optionalParams) {
        if (!this.levelEnabled(constants_1.LogLevel.Error))
            return;
        const { messages, context, stack } = this.getContextAndStackAndMessagesToPrint([message, ...optionalParams]);
        this.printMessages(messages, context, constants_1.LogLevel.Error, 'stderr');
        this.printStackTrace(stack);
    }
    warn(message, ...optionalParams) {
        if (!this.levelEnabled(constants_1.LogLevel.Warn))
            return;
        const { messages, context } = this.getContextAndMessagesToPrint([message, ...optionalParams]);
        this.printMessages(messages, context, constants_1.LogLevel.Warn);
    }
    debug(message, ...optionalParams) {
        if (!this.levelEnabled(constants_1.LogLevel.Debug))
            return;
        const { messages, context } = this.getContextAndMessagesToPrint([message, ...optionalParams]);
        this.printMessages(messages, context, constants_1.LogLevel.Debug);
    }
    getTimestamp() {
        return dateTimeFormatter.format(Date.now());
    }
    /**
     * Note: No level is enabled if suppressLogging is true
     */
    levelEnabled(logLevel) {
        if (this.options?.suppressLogging)
            return false;
        return constants_1.LevelRank[logLevel] >= constants_1.LevelRank[getLogLevel()];
    }
    printMessages(messages, context = '', logLevel = constants_1.LogLevel.Log, writeStreamType) {
        messages.forEach(message => {
            this.printMessage(message, context, logLevel, writeStreamType);
        });
    }
    printMessage(message, context = '', logLevel = constants_1.LogLevel.Log, writeStreamType) {
        const formattedMessage = this.formatMessage(logLevel, message, context);
        if (process[writeStreamType ?? 'stdout'])
            process[writeStreamType ?? 'stdout'].write(formattedMessage);
        else
            this.consoleLogMessage(formattedMessage, logLevel);
    }
    formatPid(pid) {
        const serviceContext = this.options?.serviceContext ?? process.env.SERVICE_CONTEXT ?? 'E11';
        return `[${serviceContext}] ${pid ?? ''}  - `;
    }
    formatContext(context) {
        if (!context)
            return '';
        const contextFormat = `[${context}] `;
        if (this.options?.noColor)
            return contextFormat;
        return (0, cli_color_util_1.yellow)(contextFormat);
    }
    printStackTrace(stack) {
        if (!stack) {
            return;
        }
        process.stderr.write(`${stack}\n`);
    }
    updateAndGetTimestampDiff() {
        const includeTimestamp = E11Logger.lastTimestampAt && this.options?.timestamp;
        const result = includeTimestamp ? this.formatTimestampDiff(Date.now() - E11Logger.lastTimestampAt) : '';
        E11Logger.lastTimestampAt = Date.now();
        return result;
    }
    formatTimestampDiff(timestampDiff) {
        return (0, cli_color_util_1.yellow)(` +${timestampDiff}ms`);
    }
    formatMessage(logLevel, message, context) {
        let pidMessage = this.formatPid(process.pid);
        let formattedLogLevel = logLevel.toUpperCase().padStart(7, ' ');
        const contextMessage = this.formatContext(context);
        const timestampDiff = this.updateAndGetTimestampDiff();
        const output = this.stringifyMessage(message, logLevel);
        pidMessage = this.colorize(pidMessage, logLevel);
        formattedLogLevel = this.colorize(formattedLogLevel, logLevel);
        return `${pidMessage}${this.getTimestamp()} ${formattedLogLevel} ${contextMessage}${output}${timestampDiff}\n`;
    }
    stringifyMessage(message, logLevel) {
        const opts = { space: 2 };
        // If the message is a function, call it and re-resolve its value.
        if (typeof message === 'function')
            return this.stringifyMessage(message(), logLevel);
        else if (this.isError(message))
            return `${message.stack}\n ${(0, stringify_object_1.stringifyObject)(this.formatError(message), opts)}`;
        else if (isPlainObject(message) || Array.isArray(message))
            return `${(0, stringify_object_1.stringifyObject)(message, opts)}`;
        else
            return this.colorize(message, logLevel);
    }
    getContextAndMessagesToPrint(args) {
        if (this.context) {
            return {
                context: this.context,
                messages: args,
            };
        }
        if (args?.length <= 1) {
            return { messages: args, context: this.context };
        }
        const lastElement = args[args.length - 1];
        const isContext = typeof lastElement === 'string';
        if (!isContext) {
            return { messages: args, context: this.context };
        }
        return {
            context: lastElement,
            messages: args.slice(0, args.length - 1),
        };
    }
    getContextAndStackAndMessagesToPrint(args) {
        const { messages, context } = this.getContextAndMessagesToPrint(args);
        if (messages?.length <= 1) {
            return { messages, context };
        }
        const lastElement = messages[messages.length - 1];
        const isStack = typeof lastElement === 'string';
        // https://github.com/nestjs/nest/issues/11074#issuecomment-1421680060
        if (!isStack && !(typeof lastElement === 'undefined')) {
            return {
                messages: isDisplayableError(lastElement) ? messages.slice(0, messages.length - 1) : messages,
                context,
                stack: lastElement.stack,
            };
        }
        return {
            stack: lastElement,
            messages: messages.slice(0, messages.length - 1),
            context,
        };
    }
    colorize(message, logLevel) {
        if (this.options?.noColor)
            return message;
        const color = this.getColorByLogLevel(logLevel);
        return color(message);
    }
    getColorByLogLevel(level) {
        switch (level) {
            case 'debug':
                return cli_color_util_1.clc.magentaBright;
            case 'warn':
                return cli_color_util_1.clc.yellow;
            case 'error':
                return cli_color_util_1.clc.red;
            default:
                return cli_color_util_1.clc.green;
        }
    }
    consoleLogMessage(message, logLevel) {
        const levelToConsole = {
            [constants_1.LogLevel.Debug]: console.debug,
            [constants_1.LogLevel.Log]: console.log,
            [constants_1.LogLevel.Warn]: console.warn,
            [constants_1.LogLevel.Error]: console.error,
            [constants_1.LogLevel.None]: () => { },
        };
        levelToConsole[logLevel](message);
    }
    isError(unk) {
        return unk instanceof Error || !!(unk.stack && unk.message);
    }
    // any because Error type compile errors due to spread
    formatError(e) {
        return { name: e.name, cause: e.cause, message: e.message, ...e };
    }
}
exports.E11Logger = E11Logger;
const isPlainObject = (item) => typeof item === 'object' && !Array.isArray(item);
const isDisplayableError = (item) => item.stack !== undefined;
